import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Pagination,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Card,
  CardContent,
  Stack,
  Fade,
  Zoom,
  Container,
  useTheme,
  alpha,
  useMediaQuery,
} from '@mui/material';
import {
  MoreVert,
  ConfirmationNumber,
  Close,
  KeyboardArrowRight,
  ArrowBack,
} from '@mui/icons-material';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { errorTost, successTost } from '../../Services/Toast';
import TicketDetailsModal from './TicketDetailsModal';
import {
  HELPDESK_BASE_URL,
  HELPDESK_TENANT_ID,
  buildHelpDeskUser,
} from './helpDeskConfig';

const PRIMARY = '#1570EF';

const TicketHistory = ({ onBack }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:1000px)');

  const loggedInUser = useSelector((state) => state.Auth.user);
  const hdUser = buildHelpDeskUser(loggedInUser);

  const [tickets, setTickets] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [loading, setLoading] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const openModal = (ticket) => {
    setSelectedTicket(ticket);
    setModalOpen(true);
  };
  const closeModal = () => {
    setSelectedTicket(null);
    setModalOpen(false);
  };

  const formatDisplayDate = (isoDate) => {
    if (!isoDate) return '';
    return new Date(isoDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${HELPDESK_BASE_URL}/api/user/getTicketByCustomerMobileandEmail`,
        {
          email: hdUser.email,
          mobile: hdUser.mobile || '',
          // CRM: tickets are scoped to the logged-in CRM user (other_id)
          id: hdUser.crmUserId,
          page,
          pageSize,
          tenant_id: HELPDESK_TENANT_ID,
        }
      );

      if (response.data.success) {
        setTickets(response.data.data);
      } else {
        setTickets([]);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event, ticketId) => {
    setAnchorEl(event.currentTarget);
    setSelectedTicketId(ticketId);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTicketId(null);
  };

  const handleCloseTicket = async () => {
    try {
      const res = await axios.post(
        `${HELPDESK_BASE_URL}/api/user/ticket/update-status`,
        { status: 'closed', ticketId: selectedTicketId }
      );
      if (res.data.success) {
        successTost('Ticket closed successfully');
        handleMenuClose();
        fetchTickets();
      } else {
        errorTost('Failed to close ticket');
      }
    } catch (err) {
      console.error('Error closing ticket:', err);
      errorTost('Error occurred while closing ticket');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'resolved':
        return 'success';
      case 'open':
        return 'warning';
      case 'closed':
        return 'default';
      default:
        return 'primary';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return theme.palette.error.main;
      case 'medium':
        return theme.palette.warning.main;
      case 'low':
        return theme.palette.success.main;
      default:
        return theme.palette.info.main;
    }
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', backgroundColor: '#eef0f3', py: 2 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper
          elevation={0}
          sx={{ p: 1.2, mb: 3, borderRadius: 3, background: PRIMARY, color: 'white' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton
              onClick={() => (onBack ? onBack() : window.history.back())}
              sx={{ color: 'white', '&:hover': { bgcolor: alpha('#fff', 0.1) } }}>
              <ArrowBack />
            </IconButton>
            {!isMobile && <ConfirmationNumber sx={{ fontSize: 28 }} />}
            <Typography sx={{ fontWeight: 700, flexGrow: 1, fontSize: isMobile ? 20 : 25 }}>
              All Tickets
            </Typography>
            <Avatar
              sx={{
                bgcolor: 'white',
                color: PRIMARY,
                width: isMobile ? 30 : 40,
                height: isMobile ? 30 : 40,
              }}>
              {tickets.length}
            </Avatar>
          </Stack>
        </Paper>

        {loading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 400,
              gap: 2,
            }}>
            <CircularProgress size={60} thickness={4} />
            <Typography variant="h6" color="text.secondary" sx={{ fontFamily: 'Roboto-Regular' }}>
              Loading your tickets...
            </Typography>
          </Box>
        ) : tickets.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              bgcolor: alpha(theme.palette.background.paper, 0.8),
            }}>
            <ConfirmationNumber sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" color="text.secondary" sx={{ fontFamily: 'Roboto-Regular' }} gutterBottom>
              No tickets found
            </Typography>
            <Typography variant="body1" sx={{ fontFamily: 'Roboto-Regular' }} color="text.secondary">
              You haven't created any tickets yet.
            </Typography>
          </Paper>
        ) : (
          <Fade in timeout={800}>
            <Box>
              <Grid container spacing={3}>
                {tickets.map((ticket, index) => (
                  <Grid item xs={12} md={6} lg={4} key={ticket.id}>
                    <Zoom in timeout={600 + index * 100}>
                      <Card
                        elevation={0}
                        sx={{
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          cursor: 'pointer',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.1)}`,
                          },
                          fontFamily: 'Roboto-Regular',
                        }}>
                        <CardContent sx={{ p: 2, pb: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Ticket ID + Status */}
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 600, fontSize: 12, fontFamily: 'Roboto-Regular' }}>
                              {ticket.display_id}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Chip
                                label={ticket.status?.toUpperCase()}
                                color={getStatusColor(ticket.status)}
                                sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', fontFamily: 'Roboto-Regular' }}
                              />
                              {ticket.status?.toLowerCase() === 'resolved' && (
                                <IconButton
                                  onClick={(e) => handleMenuClick(e, ticket.id)}
                                  sx={{ color: 'text.secondary', padding: '4px' }}>
                                  <MoreVert sx={{ fontSize: 18 }} />
                                </IconButton>
                              )}
                            </Stack>
                          </Stack>

                          {/* Category */}
                          <Typography sx={{ fontSize: 15, fontWeight: 600, fontFamily: 'Roboto-Regular' }}>
                            {ticket.category_name}
                            {ticket.subcategory_name && (
                              <span style={{ color: '#666', fontWeight: 400 }}>
                                {' • ' + ticket.subcategory_name}
                              </span>
                            )}
                          </Typography>

                          {/* Created + Updated */}
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography sx={{ color: 'text.secondary', fontFamily: 'Roboto-Regular', fontSize: 13 }}>
                              Created {formatDisplayDate(ticket.created_at)}
                            </Typography>
                            <Typography variant="body2">•</Typography>
                            <Typography sx={{ fontSize: 13, color: 'text.secondary', fontFamily: 'Roboto-Regular' }}>
                              Updated {formatDisplayDate(ticket.last_updated_at)}
                            </Typography>
                          </Stack>

                          {/* Department */}
                          <Stack direction="row" gap={1} alignItems="center">
                            <Typography variant="body2" sx={{ fontFamily: 'Roboto-Regular', fontWeight: 500 }}>
                              Department:
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'Roboto-Regular', fontWeight: 500 }}>
                              {ticket?.department_name}
                            </Typography>
                          </Stack>

                          {/* Priority */}
                          <Stack direction="row" gap={1} alignItems="center">
                            <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'Roboto-Regular' }}>
                              Priority:
                            </Typography>
                            <Typography sx={{ fontWeight: 600, fontFamily: 'Roboto-Regular', color: getPriorityColor(ticket.priority_level) }}>
                              {ticket.priority_level?.toUpperCase()}
                            </Typography>
                          </Stack>
                        </CardContent>

                        {/* Footer */}
                        <Box
                          onClick={() => openModal(ticket)}
                          sx={{
                            px: 3,
                            py: 1.8,
                            borderTop: `1px solid #f0f0f0ff`,
                            mt: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: 'pointer',
                            transition: '0.2s ease',
                            '&:hover': { bgcolor: alpha(PRIMARY, 0.05) },
                            backgroundColor: '#f7f9fa',
                          }}>
                          <Typography sx={{ fontFamily: 'Roboto-Regular', fontWeight: 600, color: PRIMARY, fontSize: 14 }}>
                            View Details
                          </Typography>
                          <KeyboardArrowRight sx={{ color: PRIMARY, fontSize: 22 }} />
                        </Box>
                      </Card>
                    </Zoom>
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: alpha(theme.palette.background.paper, 0.8) }}>
                  <Pagination
                    count={Math.ceil(tickets.length < pageSize ? page : page + 1)}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    size="large"
                    sx={{ '& .MuiPaginationItem-root': { borderRadius: 2, fontWeight: 600 } }}
                  />
                </Paper>
              </Box>
            </Box>
          </Fade>
        )}

        {/* Resolved-ticket action menu */}
        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 8,
            sx: { borderRadius: 2, minWidth: 180, '& .MuiMenuItem-root': { borderRadius: 1, mx: 1, my: 0.5 } },
          }}>
          <MenuItem
            onClick={handleCloseTicket}
            sx={{ color: 'error.main', '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) } }}>
            <Close sx={{ mr: 1, fontSize: 20 }} />
            Close Ticket
          </MenuItem>
        </Menu>
      </Container>

      {/* Ticket details */}
      <TicketDetailsModal open={modalOpen} onClose={closeModal} ticket={selectedTicket} />
    </Box>
  );
};

export default TicketHistory;
